import React from 'react';
import T from 'Components/Helpers/translate';
import { formatProspectingFilterValue } from './prospectingFilterValue';
import type { ProspectingFilterPanel } from './types';

/**
 * What the assistant did to the prospecting search, shown in the thread.
 *
 * The filters themselves live in the catalogue pane — this card is the record of
 * the turn: which filters this answer set, why each one, and which part of the
 * request became no filter at all.
 *
 * Mint, as the workspace design draws it (WEB-10886): the assistant's answer is
 * one of the brand's tinted surfaces, and purple is left to mean "the AI is
 * working" as it does everywhere else on the screen.
 */
const ProspectingFiltersCard = ({
	panel,
	onUndo
}: {
	panel: ProspectingFilterPanel;
	/** Puts back the filters from before this turn - only on the turn that can still be undone (WEB-13158). */
	onUndo?: () => void;
}) => {
	const { filters, unresolved } = panel;
	if (!filters.length && !unresolved?.length) return null;

	return (
		/* Hex rather than the workspace's tokens: the AI Workspace shows a
		   prospecting conversation too, and has no `--pw-*` variables. 8px
		   literally — `rounded-lg` is `--radius` (10px), not the design's card. */
		<div className="overflow-hidden rounded-[8px] border border-brand-light bg-[#e8f9f8]">
			<div className="flex items-center justify-between border-b border-brand-light px-3 py-[9px]">
				<span className="text-[12px] font-semibold text-brand">
					{T('prospecting.v2.filtersCard.title', { amount: filters.length })}
				</span>
				{onUndo ? (
					<button
						type="button"
						onClick={onUndo}
						className="text-[12px] font-medium text-brand underline-offset-2 hover:underline"
					>
						{T('prospecting.v2.filtersCard.undo')}
					</button>
				) : (
					<span className="font-mono text-[10px] uppercase text-[#086471]">
						{T('prospecting.v2.filtersCard.applied')}
					</span>
				)}
			</div>

			{filters.length > 0 ? (
				<ul className="m-0 flex list-none flex-col p-0">
					{filters.map((filter, index) => (
						<li
							key={filter.key}
							className={
								index < filters.length - 1 || unresolved?.length
									? 'border-b border-brand-light px-3 py-2'
									: 'px-3 py-2'
							}
						>
							<div className="flex justify-between gap-2">
								<span className="text-[12px] text-grey-10">
									{filter.labelKey ? T(filter.labelKey) : filter.key}
								</span>
								<span className="text-right font-mono text-[12px] text-grey-13 tabular-nums">
									{formatProspectingFilterValue(filter.value)}
								</span>
							</div>
							{filter.reasoning ? (
								<div className="mt-0.5 text-[11px] leading-[1.45] text-greyeen">{filter.reasoning}</div>
							) : null}
						</li>
					))}
				</ul>
			) : null}

			{unresolved?.length ? (
				<div className="px-3 py-2">
					<div className="text-[11px] text-[#086471]">{T('prospecting.v2.filtersCard.unresolved')}</div>
					<div className="text-[12px] text-grey-11">{unresolved.join(' · ')}</div>
				</div>
			) : null}
		</div>
	);
};

export default ProspectingFiltersCard;
